import React, { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './SubscriptionSuccessPage.css';

const UNLOCK_INDEXES = [0, 1, 2, 3, 4] as const;

const SubscriptionSuccessPage: React.FC = () => {
    const { t } = useTranslation('pricing');
    const { user, refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    useEffect(() => {
        if (searchParams.get('session_id') === null && !searchParams.get('from')) {
            // Deep-linked without the stripe session — bounce home after a moment
            const timer = setTimeout(() => navigate('/', { replace: true }), 4000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [searchParams, navigate]);

    return (
        <article className="subscription-success">
            <div className="subscription-success__wrap">
                <header className="subscription-success__hero">
                    <div className="subscription-success__badge" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </div>
                    <h1 className="subscription-success__title">
                        <span className="subscription-success__title-greeting">{t('success.titleGreeting')}</span>
                        <span className="subscription-success__title-brand">{t('success.titleBrand')}</span>
                    </h1>
                    <p className="subscription-success__subtitle">{t('success.subtitle')}</p>
                </header>

                <section className="subscription-success__card">
                    <h2 className="subscription-success__heading">{t('success.unlocked.heading')}</h2>
                    <ul className="subscription-success__list">
                        {UNLOCK_INDEXES.map((i) => (
                            <li key={i} className="subscription-success__item">
                                <span className="subscription-success__check" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                </span>
                                <span>{t(`success.unlocked.items.${i}`)}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {user?.email && (
                    <p className="subscription-success__receipt">
                        {t('success.receiptBefore')}
                        <span className="subscription-success__email">{user.email}</span>
                        {t('success.receiptAfter')}
                    </p>
                )}

                <div className="subscription-success__actions">
                    <Link to="/" className="subscription-success__cta">
                        {t('success.primaryCta')}
                    </Link>
                    <Link to="/pricing" className="subscription-success__secondary">
                        {t('success.secondaryCta')}
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default SubscriptionSuccessPage;
